import type { ClienteDetalle } from '../logistica/consultas'
import type { DatosCliente, EtiquetaRuta, EtiquetaSemana } from '../../api/modelos'

/**
 * Estado del formulario de cliente (RF-40, RF-30, RF-31) y su paso al cuerpo
 * de `POST /clients` o `PATCH /clients/:id`. Las coordenadas se escriben como
 * texto (admiten coma decimal) y se validan en rango antes de mandar nada.
 */
export interface ValoresCliente {
  nombre: string
  nif: string
  movil: string
  telefono: string
  email: string
  direccion: string
  codigo_postal: string
  ciudad: string
  provincia: string
  notas: string
  ruta: EtiquetaRuta | ''
  semana: EtiquetaSemana | ''
  granEmpresa: boolean
  latitud: string
  longitud: string
}

const TEXTOS = ['nombre', 'nif', 'movil', 'telefono', 'email', 'direccion', 'codigo_postal', 'ciudad', 'provincia', 'notas'] as const

export const VACIO: ValoresCliente = {
  nombre: '', nif: '', movil: '', telefono: '', email: '', direccion: '', codigo_postal: '',
  ciudad: '', provincia: '', notas: '', ruta: '', semana: '', granEmpresa: false, latitud: '', longitud: '',
}

const RUTAS = new Set(['r1', 'r2', 'r3', 'r4', 'r5'])
const SEMANAS = new Set(['s1', 's2', 's3', 's4'])

/** Si la ficha no trae `ruta`/`semana`/`granEmpresa`, se deducen de sus etiquetas. */
export function valoresDeFicha(ficha: ClienteDetalle): ValoresCliente {
  const etiquetas = (ficha.etiquetas ?? []).map((e) => e.toLowerCase())
  const ruta = ficha.ruta ?? (etiquetas.find((e) => RUTAS.has(e)) as EtiquetaRuta | undefined) ?? ''
  const semana = ficha.semana ?? (etiquetas.find((e) => SEMANAS.has(e)) as EtiquetaSemana | undefined) ?? ''
  const valores: ValoresCliente = { ...VACIO, ruta, semana, granEmpresa: ficha.granEmpresa ?? etiquetas.includes('ge') }
  for (const campo of TEXTOS) valores[campo] = String(ficha[campo] ?? '')
  valores.latitud = ficha.latitud === null || ficha.latitud === undefined ? '' : String(ficha.latitud)
  valores.longitud = ficha.longitud === null || ficha.longitud === undefined ? '' : String(ficha.longitud)
  return valores
}

function numero(texto: string): number | null {
  const limpio = texto.trim().replace(',', '.')
  if (limpio === '') return null
  const n = Number(limpio)
  return Number.isFinite(n) ? n : Number.NaN
}

/** Errores de validación del lado del panel; vacío = se puede mandar. */
export function validarCliente(v: ValoresCliente): string[] {
  const errores: string[] = []
  if (v.nombre.trim() === '') errores.push('El nombre es obligatorio.')
  const lat = numero(v.latitud)
  const lng = numero(v.longitud)
  if ((lat === null) !== (lng === null)) errores.push('Escribe la latitud y la longitud, o deja las dos vacías.')
  if (lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) errores.push('La latitud tiene que estar entre -90 y 90.')
  if (lng !== null && (Number.isNaN(lng) || lng < -180 || lng > 180)) errores.push('La longitud tiene que estar entre -180 y 180.')
  if (v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) errores.push('El correo no es válido.')
  return errores
}

function cuerpoCompleto(v: ValoresCliente): DatosCliente {
  const cuerpo: DatosCliente = {}
  for (const campo of TEXTOS) cuerpo[campo] = v[campo].trim()
  cuerpo.ruta = v.ruta || null
  cuerpo.semana = v.semana || null
  cuerpo.granEmpresa = v.granEmpresa
  cuerpo.latitud = numero(v.latitud)
  cuerpo.longitud = numero(v.longitud)
  return cuerpo
}

/** Alta: solo lo que tiene valor (el backend rellena el resto). */
export function cuerpoAlta(v: ValoresCliente): DatosCliente {
  const completo = cuerpoCompleto(v)
  const cuerpo: DatosCliente = {}
  for (const [clave, valor] of Object.entries(completo) as [keyof DatosCliente, unknown][]) {
    if (valor === '' || valor === null) continue
    if (clave === 'granEmpresa' && valor === false) continue
    ;(cuerpo as Record<string, unknown>)[clave] = valor
  }
  return cuerpo
}

/**
 * Edición: solo los campos que han cambiado. Así un campo que la ficha no trae
 * (porque el backend aún no lo expone) no se borra al guardar otra cosa.
 */
export function cuerpoEdicion(inicial: ValoresCliente, v: ValoresCliente): DatosCliente {
  const antes = cuerpoCompleto(inicial)
  const ahora = cuerpoCompleto(v)
  const cuerpo: DatosCliente = {}
  for (const clave of Object.keys(ahora) as (keyof DatosCliente)[]) {
    if (ahora[clave] !== antes[clave]) (cuerpo as Record<string, unknown>)[clave] = ahora[clave]
  }
  return cuerpo
}

/** De baja según el backend: `activo === false` manda; la etiqueta `baja` es el respaldo. */
export function estaDeBaja(ficha: { activo?: boolean; etiquetas?: string[] } | undefined): boolean {
  if (!ficha) return false
  if (ficha.activo === false) return true
  return (ficha.etiquetas ?? []).some((e) => e.toLowerCase() === 'baja')
}
